<script setup lang="ts">
import { ref, onMounted, provide } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { bitable, type Selection } from '@lark-base-open/js-sdk'
import { templateApi, tenantApi } from '@/services/api'
import { toast } from '@/utils/toast'
import { useUIStore } from '@/stores/ui'
import { useTenantStore } from '@/stores/tenant'
import type { Template } from '@/types'
import EditionBanner from '@/components/EditionBanner.vue'
import LoadingOverlay from '@/components/LoadingOverlay.vue'
import TopBar from '@/components/TopBar.vue'
import Welcome from '@/views/Welcome.vue'

const uiStore = useUIStore()
const tenantStore = useTenantStore()
const { t } = useI18n()
const router = useRouter()

const templates = ref<Template[]>([])
const activeTemplate = ref<string | null>(null)
const loading = ref(true)
const baseToken = ref<string | null>(null)
const tableId = ref<string | null>(null)
const isInLarkEnvironment = ref(true)
const checkingEnvironment = ref(true)

const getActiveTemplate = (): Template | null => {
  return templates.value.find(t => t.id === activeTemplate.value) || null
}

const getTemplateStatus = (template: Template): string => {
  return template.r2_key ? 'ready' : 'setup-required'
}

// Provide data to child components
provide('templates', templates)
provide('activeTemplate', activeTemplate)
provide('baseToken', baseToken)
provide('tableId', tableId)

const createNewTemplate = async (): Promise<void> => {
  if (!baseToken.value || !tableId.value) return

  try {
    const newTemplate = await templateApi.create(baseToken.value, tableId.value, {
      name: `${t('untitledTemplate')} ${templates.value.length + 1}`
    })

    templates.value.push(newTemplate)
    activeTemplate.value = newTemplate.id

    // Auto-navigate to setup for new templates
    router.push('/template-setup')

    toast.success(t('templateCreated'))
  } catch (error) {
    console.error('Failed to create template:', error)
    toast.error(error instanceof Error ? error.message : t('failedToCreateTemplate'))
  }
}

const loadTenantInfo = async (): Promise<boolean> => {
  try {
    // Don't check if we're already on the my-company page
    if (router.currentRoute.value.path === '/my-company') {
      return true
    }

    const { tenant, base } = await tenantApi.getTenantInfo()
    tenantStore.setTenantAndBase(tenant, base)

    // Navigation guard in router will handle redirecting to My Company if needed
    // This check is now redundant but kept for backwards compatibility
    if (!base?.personal_base_token) {
      console.log('Personal base token not set for this base, redirecting to setup')
      router.push('/my-company')
      return false
    }

    return true
  } catch (error) {
    console.error('Failed to load tenant info:', error)
    return true // Don't block if check fails
  }
}


const loadTemplates = async (): Promise<void> => {
  if (!baseToken.value || !tableId.value) return

  try {
    loading.value = true
    const loadedTemplates = await templateApi.list(baseToken.value, tableId.value)
    templates.value = loadedTemplates

    // Check if currently active template still exists
    const activeTemplateExists = activeTemplate.value &&
      loadedTemplates.some(t => t.id === activeTemplate.value)

    // Set first template as active if:
    // 1. No template is selected, OR
    // 2. The selected template no longer exists (was deleted)
    if (loadedTemplates.length > 0 && !activeTemplateExists && loadedTemplates[0]) {
      activeTemplate.value = loadedTemplates[0].id
    } else if (loadedTemplates.length === 0) {
      // No templates available, clear selection
      activeTemplate.value = null
    }
  } catch (error) {
    console.error('Failed to load templates:', error)
    toast.error(error instanceof Error ? error.message : t('failedToLoadTemplates'))
  } finally {
    loading.value = false
  }
}

onMounted(async () => {
  try {
    // Timeout promise - if getSelection takes longer than 2s, we're not in Lark
    const timeoutPromise = new Promise<Selection>((_, reject) => {
      setTimeout(() => reject(new Error('Lark environment check timeout')), 2000)
    })

    // Race between getSelection and timeout
    const selection = await Promise.race([
      bitable.base.getSelection(),
      timeoutPromise
    ])

    // Check if we got valid selection data (inside Lark environment)
    if (!selection || !selection.baseId || !selection.tableId) {
      console.log('Not in valid Lark environment - no selection data')
      isInLarkEnvironment.value = false
      loading.value = false
      checkingEnvironment.value = false
      return
    }

    baseToken.value = selection.baseId
    tableId.value = selection.tableId
    checkingEnvironment.value = false

    // Always load tenant info first (needed for app-wide access)
    // This also checks if setup is complete and redirects to /my-company if not
    const setupComplete = await loadTenantInfo()
    if (!setupComplete) {
      loading.value = false
      return
    }

    await loadTemplates()

    // Listen for table/view changes in Lark
    bitable.base.onSelectionChange(async (event) => {
      try {
        const newSelection = event.data

        // Check if table or base changed
        const tableChanged = newSelection.tableId && newSelection.tableId !== tableId.value
        const baseChanged = newSelection.baseId && newSelection.baseId !== baseToken.value

        if (tableChanged || baseChanged) {
          console.log('Table or base changed, reloading templates...', {
            oldTable: tableId.value,
            newTable: newSelection.tableId,
            oldBase: baseToken.value,
            newBase: newSelection.baseId
          })

          // Update context
          if (newSelection.baseId) baseToken.value = newSelection.baseId
          if (newSelection.tableId) tableId.value = newSelection.tableId

          // Reset active template since templates are table-specific
          activeTemplate.value = null

          // If base changed, check setup and reload tenant info
          if (baseChanged && baseToken.value) {
            const setupComplete = await loadTenantInfo()
            if (!setupComplete) {
              return
            }
          }

          // Reload templates for the new table
          await loadTemplates()
        }
      } catch (error) {
        console.error('Error handling selection change:', error)
        toast.error(error instanceof Error ? error.message : t('failedToLoadTemplates'))
      }
    })
  } catch (error) {
    console.error('Not in Lark environment:', error)

    // If getSelection fails or times out, we're not in a valid Lark environment
    isInLarkEnvironment.value = false
    loading.value = false
    checkingEnvironment.value = false
  }
})
</script>

<template>
  <!-- Checking Lark Environment -->
  <div v-if="checkingEnvironment" class="flex flex-col h-screen bg-white items-center justify-center">
    <div class="flex flex-col items-center gap-4">
      <div class="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
      <p class="text-sm font-medium text-gray-700">{{ t('loading') }}</p>
    </div>
  </div>

  <!-- Redirect to welcome page if not in Lark environment -->
  <div v-else-if="!isInLarkEnvironment">
    <Welcome />
  </div>

  <!-- Normal app layout -->
  <div v-else class="flex flex-col h-screen bg-white">
    <!-- Loading Overlay -->
    <LoadingOverlay :show="uiStore.isLoading" />

    <!-- Top Banner -->
    <EditionBanner />

    <!-- Main Content -->
    <div class="flex-1 flex flex-col overflow-hidden">
      <!-- Top Bar - Always visible for navigation -->
      <TopBar
        :active-template="getActiveTemplate()"
        :all-templates="templates"
        :get-template-status="getTemplateStatus"
        @template-selected="(id: string) => activeTemplate = id"
        @create-template="createNewTemplate"
      />

      <!-- Router View -->
      <router-view
        class="flex-1 overflow-hidden"
        :activeTemplate="getActiveTemplate()"
        :allTemplates="templates"
        :baseToken="baseToken"
        :tableId="tableId"
        @template-updated="loadTemplates"
        @template-deleted="loadTemplates"
        @template-selected="(id: string) => activeTemplate = id"
        @create-template="createNewTemplate"
      />
    </div>
  </div>
</template>
